import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Phone, Mail, Globe, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const { settings } = useOutletContext();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-black">
      <div className="container-x section grid lg:grid-cols-2 gap-12">
        <div>
          <div className="eyebrow mb-3">Get In Touch</div>
          <h1 className="font-heading text-4xl md:text-5xl mb-6">Let's Talk Music.</h1>
          <p className="text-white/70 mb-10">
            Tell us about your brand and the song you imagine. We'll respond within one business day.
          </p>
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 border border-gold/50 flex items-center justify-center bg-ink-900">
                <Phone className="text-gold" size={18} />
              </div>
              <div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-semibold">Phone</div>
                <div className="text-white">{settings?.phone}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 border border-gold/50 flex items-center justify-center bg-ink-900">
                <Mail className="text-gold" size={18} />
              </div>
              <div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-semibold">Email</div>
                <div className="text-white">{settings?.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 border border-gold/50 flex items-center justify-center bg-ink-900">
                <Globe className="text-gold" size={18} />
              </div>
              <div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-semibold">Website</div>
                <div className="text-white">{settings?.website}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-ink-900 border border-ink-700 p-8">
          {done ? (
            <div className="flex flex-col items-center text-center py-12" data-testid="contact-success">
              <CheckCircle2 className="text-gold mb-4" size={56} />
              <h3 className="font-heading text-2xl mb-2">Message Sent</h3>
              <p className="text-white/60">Thanks! We'll be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="label">Full Name</label>
                  <input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input required type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="label">Phone</label>
                  <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
                </div>
                <div>
                  <label className="label">Company</label>
                  <input className="input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="contact-company" />
                </div>
              </div>
              <div>
                <label className="label">Subject</label>
                <input className="input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
              </div>
              <div>
                <label className="label">Message</label>
                <textarea required rows={5} className="input resize-none" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
              </div>
              {error && <div className="text-destructive text-sm" data-testid="contact-error">{error}</div>}
              <button type="submit" disabled={submitting} className="btn-gold" data-testid="contact-submit">
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
